import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Save, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { CardPromo, CreateCardPromoRequest, UpdateCardPromoRequest } from '@/types'

interface CardPromoDrawerProps {
  mode: 'create' | 'edit'
  initial?: CardPromo | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Display order Mon-first; values use the backend's Go convention (0=Sunday). */
const DAY_OPTIONS: Array<{ value: number; label: string; full: string }> = [
  { value: 1, label: 'Mon', full: 'Monday' },
  { value: 2, label: 'Tue', full: 'Tuesday' },
  { value: 3, label: 'Wed', full: 'Wednesday' },
  { value: 4, label: 'Thu', full: 'Thursday' },
  { value: 5, label: 'Fri', full: 'Friday' },
  { value: 6, label: 'Sat', full: 'Saturday' },
  { value: 0, label: 'Sun', full: 'Sunday' },
]

const ALL_DAY_VALUES = DAY_OPTIONS.map((d) => d.value)

/** Plain-English echo of the chip row, so the selection is legible without
 *  re-reading seven chips ("Runs Mon, Tue." / "Runs every day."). */
function describeDays(days: number[]): string {
  if (days.length === 7) return 'every day'
  return DAY_OPTIONS.filter((d) => days.includes(d.value))
    .map((d) => d.label)
    .join(', ')
}

interface DraftState {
  bankName: string
  cardLabel: string
  discountPercent: string
  maxDiscount: string
  minBill: string
  days: number[]
  validFrom: string
  validTo: string
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  bankName: '',
  cardLabel: '',
  discountPercent: '',
  maxDiscount: '',
  minBill: '',
  days: [0, 1, 2, 3, 4, 5, 6],
  validFrom: '',
  validTo: '',
  isActive: true,
}

function promoToDraft(p: CardPromo): DraftState {
  return {
    bankName: p.bank_name,
    cardLabel: p.card_label,
    discountPercent: String(p.discount_percent),
    maxDiscount: p.max_discount_amount != null ? String(p.max_discount_amount) : '',
    minBill: p.min_bill_amount != null ? String(p.min_bill_amount) : '',
    days: p.days_of_week?.length ? [...p.days_of_week] : [0, 1, 2, 3, 4, 5, 6],
    validFrom: p.valid_from ?? '',
    validTo: p.valid_to ?? '',
    isActive: p.is_active,
  }
}

/**
 * Create/edit drawer for a single bank card promo. Keep this thin: the
 * drawer is the form; the catalog lives in AdminCardPromos.tsx.
 */
export function CardPromoDrawer({ mode, initial, open, onOpenChange }: CardPromoDrawerProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  useEffect(() => {
    if (!open) return
    setDraft(initial ? promoToDraft(initial) : EMPTY_DRAFT)
  }, [open, initial])

  const createMutation = useMutation({
    mutationFn: (data: CreateCardPromoRequest) => apiClient.createCardPromo(data),
    onSuccess: () => {
      toast({ title: 'Card promo created', description: 'It appears on the counter when a Card payment is selected.' })
      queryClient.invalidateQueries({ queryKey: ['card-promos'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCardPromoRequest }) =>
      apiClient.updateCardPromo(id, data),
    onSuccess: () => {
      toast({ title: 'Card promo updated' })
      queryClient.invalidateQueries({ queryKey: ['card-promos'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Update failed'),
  })

  const archiveMutation = useMutation({
    mutationFn: (id: string) => apiClient.archiveCardPromo(id),
    onSuccess: () => {
      toast({ title: 'Card promo archived' })
      queryClient.invalidateQueries({ queryKey: ['card-promos'] })
      onOpenChange(false)
    },
    onError: (err) => surfaceError(err, toast, 'Archive failed'),
  })

  const submitting =
    createMutation.isPending || updateMutation.isPending || archiveMutation.isPending

  function toggleDay(value: number) {
    setDraft((d) => {
      const has = d.days.includes(value)
      const next = has ? d.days.filter((x) => x !== value) : [...d.days, value]
      return { ...d, days: next }
    })
  }

  function handleSave() {
    if (!draft.bankName.trim()) {
      toast({ title: 'Bank required', description: 'Enter the bank name, e.g. HBL.', variant: 'destructive' })
      return
    }
    if (!draft.cardLabel.trim()) {
      toast({ title: 'Card label required', description: 'Name the card as the cashier will see it, e.g. "HBL Platinum credit".', variant: 'destructive' })
      return
    }
    const pct = Number(draft.discountPercent)
    if (!draft.discountPercent.trim() || !Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast({ title: 'Invalid percent', description: 'Discount must be between 0 and 100.', variant: 'destructive' })
      return
    }
    const maxDiscNum = draft.maxDiscount.trim() === '' ? undefined : Number(draft.maxDiscount)
    if (maxDiscNum !== undefined && (!Number.isFinite(maxDiscNum) || maxDiscNum <= 0)) {
      toast({ title: 'Invalid cap', description: 'Max discount must be a positive amount.', variant: 'destructive' })
      return
    }
    const minBillNum = draft.minBill.trim() === '' ? undefined : Number(draft.minBill)
    if (minBillNum !== undefined && (!Number.isFinite(minBillNum) || minBillNum < 0)) {
      toast({ title: 'Invalid minimum', description: 'Minimum bill must be a number.', variant: 'destructive' })
      return
    }
    if (draft.days.length === 0) {
      toast({ title: 'Pick a day', description: 'The promo must run on at least one day of the week.', variant: 'destructive' })
      return
    }

    if (mode === 'create') {
      const payload: CreateCardPromoRequest = {
        bank_name: draft.bankName.trim(),
        card_label: draft.cardLabel.trim(),
        discount_percent: pct,
        max_discount_amount: maxDiscNum,
        min_bill_amount: minBillNum,
        days_of_week: draft.days,
        valid_from: draft.validFrom.trim() || undefined,
        valid_to: draft.validTo.trim() || undefined,
      }
      createMutation.mutate(payload)
      return
    }
    if (!initial) return
    const data: UpdateCardPromoRequest = {
      bank_name: draft.bankName.trim(),
      card_label: draft.cardLabel.trim(),
      discount_percent: pct,
      days_of_week: draft.days,
      is_active: draft.isActive,
    }
    if (maxDiscNum === undefined) {
      data.clear_max_discount = true
    } else {
      data.max_discount_amount = maxDiscNum
    }
    if (minBillNum === undefined) {
      data.clear_min_bill = true
    } else {
      data.min_bill_amount = minBillNum
    }
    if (draft.validFrom.trim() === '') {
      data.clear_valid_from = true
    } else {
      data.valid_from = draft.validFrom.trim()
    }
    if (draft.validTo.trim() === '') {
      data.clear_valid_to = true
    } else {
      data.valid_to = draft.validTo.trim()
    }
    updateMutation.mutate({ id: initial.id, data })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'New bank card promo' : `Edit ${initial?.card_label ?? ''}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'A percentage off when the customer pays with a specific bank card. The cashier picks it from a list after selecting Card at checkout.'
              : 'Update the terms. Past redemptions keep the values they were applied with.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cp-bank">Bank <span className="text-destructive">*</span></Label>
              <Input
                id="cp-bank"
                value={draft.bankName}
                onChange={(e) => setDraft({ ...draft, bankName: e.target.value })}
                placeholder="HBL"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-label">Card label <span className="text-destructive">*</span></Label>
              <Input
                id="cp-label"
                value={draft.cardLabel}
                onChange={(e) => setDraft({ ...draft, cardLabel: e.target.value })}
                placeholder="HBL Platinum credit"
                maxLength={120}
              />
              <p className="text-sm text-muted-foreground">
                What the cashier sees on the quick-pick chip — include the tier if the bank's tiers get different discounts.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="cp-pct">Discount % <span className="text-destructive">*</span></Label>
              <Input
                id="cp-pct"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="0.01"
                value={draft.discountPercent}
                onChange={(e) => setDraft({ ...draft, discountPercent: e.target.value })}
                placeholder="e.g. 20"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-cap">Max discount (PKR)</Label>
              <Input
                id="cp-cap"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={draft.maxDiscount}
                onChange={(e) => setDraft({ ...draft, maxDiscount: e.target.value })}
                placeholder="e.g. 750"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-min">Min bill (PKR)</Label>
              <Input
                id="cp-min"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={draft.minBill}
                onChange={(e) => setDraft({ ...draft, minBill: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Days of the week</Label>
              {/* Default is all seven on. Without a one-tap clear, narrowing to
                  "Mondays with HBL" means six deselect taps. */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, days: ALL_DAY_VALUES }))}
                  disabled={draft.days.length === 7}
                  className="rounded px-2 py-0.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                  Select all
                </button>
                <span className="text-muted-foreground/40" aria-hidden>·</span>
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, days: [] }))}
                  disabled={draft.days.length === 0}
                  className="rounded px-2 py-0.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
            </div>
            {/* Selected = filled emerald + check; unselected = dashed outline.
                Two independent cues (colour AND the check/shape) so the state
                reads at a glance and survives colour-blindness — the plain
                dark/light fill it replaced was ambiguous on open, when all
                seven are on by default. */}
            <div className="flex flex-wrap gap-1.5">
              {DAY_OPTIONS.map((d) => {
                const on = draft.days.includes(d.value)
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    aria-pressed={on}
                    aria-label={`${d.full} — ${on ? 'included' : 'not included'}`}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                      on
                        ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 dark:border-emerald-500 dark:bg-emerald-600'
                        : 'border-dashed border-border bg-background text-muted-foreground hover:border-emerald-500/60 hover:text-foreground'
                    )}
                  >
                    {on ? (
                      <Check className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <span className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {d.label}
                  </button>
                )
              })}
            </div>
            <p className="text-sm text-muted-foreground">
              {draft.days.length === 0 ? (
                <span className="font-medium text-destructive">
                  No days selected — pick at least one or the promo never shows at the counter.
                </span>
              ) : (
                <>
                  <span className="font-medium text-foreground">Runs {describeDays(draft.days)}.</span>{' '}
                  Bank promos are often day-specific ("Mondays with HBL"). The counter only shows the
                  promo on these days.
                </>
              )}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cp-from">Valid from</Label>
              <Input
                id="cp-from"
                type="date"
                value={draft.validFrom}
                onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-to">Valid to</Label>
              <Input
                id="cp-to"
                type="date"
                value={draft.validTo}
                onChange={(e) => setDraft({ ...draft, validTo: e.target.value })}
              />
            </div>
          </div>

          {mode === 'edit' && (
            <div className="flex items-center justify-between rounded-md border border-border/70 bg-muted/30 px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="cp-active" className="text-sm">Active</Label>
                <p className="text-sm text-muted-foreground">
                  Inactive promos never appear at the counter.
                </p>
              </div>
              <Switch
                id="cp-active"
                checked={draft.isActive}
                onCheckedChange={(checked) => setDraft({ ...draft, isActive: checked })}
              />
            </div>
          )}

          {mode === 'edit' && initial && (
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border/70 bg-background/60 px-3 py-2 text-sm">
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-sm">Redemptions</div>
                <div className="mt-0.5 font-semibold tabular-nums">{initial.redemption_count}</div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-sm">
                  To claim from bank (PKR)
                </div>
                <div className="mt-0.5 font-semibold tabular-nums">
                  {initial.discount_attributed_pkr.toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <div>
            {mode === 'edit' && initial && initial.is_active && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => archiveMutation.mutate(initial.id)}
                disabled={submitting}
                className="text-destructive hover:text-destructive gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Archive
              </Button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={submitting} className="gap-1.5">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function surfaceError(err: unknown, toast: ReturnType<typeof useToast>['toast'], title: string) {
  const msg = err instanceof Error ? err.message : String(err)
  toast({ title, description: msg, variant: 'destructive' })
}
